import { useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useHasRole } from '@/hooks/useHasRole';
import { AppLayout } from '@/components/layout/AppLayout';
import { useAdminUser360, type GrowthOpportunity, type AdminSubscriptionInfo } from '@/hooks/useAdminUsers';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { SignedAvatar } from '@/components/ui/signed-avatar';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, Copy, CheckCircle2, Circle, Activity, HeartPulse,
  ClipboardList, CalendarClock, Users, Bell, AlertTriangle, Crown, Mail, Target, Send,
} from 'lucide-react';
import { ActivationNudgeDialog } from '@/components/admin/ActivationNudgeDialog';

import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Area, AreaChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis } from 'recharts';

const PERIODS = [7, 30, 90] as const;

const ACCOUNT_LABELS: Record<string, string> = {
  b2b_manager: 'Gestor de empresa',
  b2b_coordinator: 'Coordenador',
  b2b_caregiver: 'Cuidador de empresa',
  b2c_owner: 'Familiar responsável',
  b2c_caregiver: 'Cuidador autônomo',
  observer: 'Observador',
  no_role: 'Sem vínculo',
};

const FUNNEL_LABELS: Record<string, string> = {
  signup: 'Cadastro',
  patient_created: 'Paciente criado',
  care_plan_created: 'Plano de cuidado criado',
  shift_template_created: 'Escala configurada',
  team_invited: 'Equipe convidada',
  team_joined: 'Convite aceito',
  first_shift: 'Primeiro plantão',
  first_task_executed: 'Primeira tarefa executada',
  subscribed: 'Assinatura ativa',
};

const TASK_TYPE_LABELS: Record<string, string> = {
  fixed_medication: 'Medicação fixa',
  sporadic_medication: 'Medicação SOS',
  vital_sign: 'Sinais vitais',
  hygiene: 'Higiene',
  food: 'Alimentação',
  physical_activity: 'Atividade física',
  incident: 'Intercorrência',
  other: 'Outros',
};

const HEALTH_LABELS: Record<string, { label: string; cls: string }> = {
  healthy: { label: 'Engajado', cls: 'bg-success/15 text-success' },
  at_risk: { label: 'Em risco', cls: 'bg-warning/15 text-warning' },
  cooling: { label: 'Esfriando', cls: 'bg-warning/15 text-warning' },
  dormant: { label: 'Dormente', cls: 'bg-destructive/15 text-destructive' },
};

const PRIORITY_LABELS: Record<string, { label: string; cls: string }> = {
  high: { label: 'Alta', cls: 'bg-destructive/15 text-destructive' },
  medium: { label: 'Média', cls: 'bg-warning/15 text-warning' },
  low: { label: 'Baixa', cls: 'bg-muted text-muted-foreground' },
};

function fmt(iso: string | null | undefined, pattern = 'dd/MM/yy HH:mm') {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), pattern, { locale: ptBR });
  } catch {
    return '—';
  }
}

function formatCurrencyCents(cents: number | null, currency = 'brl') {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: (currency || 'BRL').toUpperCase(),
  });
}

function SubBlock({ title, sub }: { title: string; sub: AdminSubscriptionInfo }) {
  const active = ['active', 'trialing'].includes(sub.status);
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <div className="flex items-center gap-1 font-medium">
        <Crown className={`h-4 w-4 ${active ? 'text-success' : 'text-muted-foreground'}`} />
        <span className={active ? '' : 'text-muted-foreground'}>{sub.status}</span>
        {sub.stripe_env === 'test' && <span className="text-[10px] text-muted-foreground">test</span>}
      </div>
      <p className="text-xs text-muted-foreground">
        {formatCurrencyCents(sub.unit_amount, sub.currency ?? 'brl')}
        {sub.interval ? ` / ${sub.interval}` : ''}
        {sub.quantity ? ` · ${sub.quantity}x` : ''}
        {sub.patient_slots ? ` · ${sub.patient_slots} vaga(s)` : ''}
      </p>
      <p className="text-xs text-muted-foreground">
        {sub.canceled_at
          ? `Cancelada em ${fmt(sub.canceled_at, 'dd/MM/yy')}`
          : `Próx. cobrança: ${fmt(sub.current_period_end, 'dd/MM/yy')}`}
      </p>
      {sub.payment_failed_at && (
        <p className="text-xs text-destructive">Falha de pagamento em {fmt(sub.payment_failed_at, 'dd/MM/yy')}</p>
      )}
    </div>
  );
}



function KPI({ icon: Icon, value, label, tone = 'primary', hint }: any) {
  const tones: Record<string, string> = {
    primary: 'bg-primary/10 text-primary',
    success: 'bg-success/10 text-success',
    warning: 'bg-warning/10 text-warning',
    destructive: 'bg-destructive/10 text-destructive',
    muted: 'bg-muted text-muted-foreground',
  };
  return (
    <Card>
      <CardContent className="p-3 flex items-start gap-3">
        <div className={`h-9 w-9 shrink-0 rounded-lg flex items-center justify-center ${tones[tone]}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0">
          <p className="text-lg font-bold leading-tight">
            {value}
            {hint ? <span className="text-xs font-normal text-muted-foreground"> · {hint}</span> : null}
          </p>
          <p className="text-xs text-muted-foreground leading-snug break-words">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}


function OpportunityCard({ o, eligible, name }: { o: GrowthOpportunity; eligible: boolean; name: string }) {
  const p = PRIORITY_LABELS[o.priority];
  const message = `${name ? `Olá, ${name.split(' ')[0]}! ` : ''}${o.suggestion}`;
  return (
    <Card>
      <CardContent className="p-4 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Badge className={`${p.cls} hover:${p.cls}`}>Prioridade {p.label}</Badge>
          {!eligible && (
            <Badge variant="outline" className="text-xs text-muted-foreground">
              Sem opt-in de marketing
            </Badge>
          )}
        </div>
        <p className="text-sm font-medium">{o.reason}</p>
        <p className="text-sm text-muted-foreground">{o.suggestion}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            navigator.clipboard.writeText(message);
            toast.success('Mensagem sugerida copiada');
          }}
        >
          <Copy className="h-3 w-3 mr-1" />Copiar mensagem
        </Button>
      </CardContent>
    </Card>
  );
}

export default function AdminUserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  const isAdmin = useHasRole('careos_admin');
  const [period, setPeriod] = useState<number>(30);
  const [nudgeOpen, setNudgeOpen] = useState(false);
  const { data, isLoading, isError, refetch, isRefetching } = useAdminUser360(userId, period);


  if (isAdmin === null) {
    return (
      <AppLayout title="Visão do usuário">
        <div className="p-4"><Skeleton className="h-12 w-48" /></div>
      </AppLayout>
    );
  }
  if (isAdmin === false) return <Navigate to="/app" replace />;

  const health = data ? HEALTH_LABELS[data.health.status] : null;

  return (
    <AppLayout title="Visão do usuário">
      <div className="container mx-auto p-4 space-y-4 max-w-6xl pb-24">
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" asChild>
            <Link to="/app/admin/users"><ArrowLeft className="h-4 w-4 mr-1" />Usuários</Link>
          </Button>
          {PERIODS.map((p) => (
            <Button key={p} size="sm" variant={period === p ? 'default' : 'outline'} onClick={() => setPeriod(p)}>
              {p} dias
            </Button>
          ))}
          <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isRefetching}>
            <RefreshCw className={`h-4 w-4 mr-1 ${isRefetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {isError && (
          <Card>
            <CardContent className="p-4 text-sm text-destructive">
              Não foi possível carregar os dados deste usuário.
            </CardContent>
          </Card>
        )}

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-24" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-20" />)}
            </div>
            <Skeleton className="h-64" />
          </div>
        ) : data ? (
          <>
            {/* Identity */}
            <Card>
              <CardContent className="p-4 flex flex-wrap items-start gap-4">
                <SignedAvatar
                  src={data.identity.avatar_url}
                  bucket="avatars"
                  className="h-14 w-14"
                  fallback={
                    <span className="bg-primary/10 text-primary text-lg flex items-center justify-center w-full h-full">
                      {(data.identity.full_name || data.identity.email || '?').charAt(0).toUpperCase()}
                    </span>
                  }
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-lg font-bold truncate">{data.identity.full_name || 'Sem nome'}</h2>
                    {health && <Badge className={`${health.cls} hover:${health.cls}`}>{health.label} · {data.health.score}/100</Badge>}
                  </div>
                  <p className="text-sm text-muted-foreground break-all">{data.identity.email}</p>
                  <div className="flex gap-2 flex-wrap text-xs text-muted-foreground">
                    <span>{ACCOUNT_LABELS[data.identity.account_type] ?? data.identity.account_type}</span>
                    {data.identity.company && <span>· {data.identity.company.name}</span>}
                    <span>· Cadastro {fmt(data.identity.created_at, 'dd/MM/yy')} ({data.identity.account_age_days}d)</span>
                    <span>· Último acesso {fmt(data.identity.last_sign_in_at)}</span>
                    {data.identity.phone && <span>· {data.identity.phone}</span>}
                    {data.identity.providers.length > 0 && <span>· {data.identity.providers.join(', ')}</span>}
                  </div>
                  {data.identity.roles.length > 0 && (
                    <div className="flex gap-1 flex-wrap pt-1">
                      {data.identity.roles.map((r) => (
                        <Badge key={r} variant="outline" className="text-xs">{r}</Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-sm space-y-3">
                  {(() => {
                    const personal = data.personal_subscription ?? (data.subscription && !data.subscription.is_company ? data.subscription : null);
                    const companySub = data.company_subscription ?? (data.subscription?.is_company ? data.subscription : null);
                    if (!personal && !companySub) return <Badge variant="outline">Plano free</Badge>;
                    return (
                      <>
                        {personal && <SubBlock title="Assinatura familiar" sub={personal} />}
                        {companySub && (
                          <SubBlock
                            title={`Assinatura da empresa${data.identity.company?.name ? ` (${data.identity.company.name})` : ''}`}
                            sub={companySub}
                          />
                        )}
                      </>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>

            {/* Growth opportunities */}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-sm font-semibold flex items-center gap-2">
                  <Target className="h-4 w-4 text-primary" />
                  Oportunidades de comunicação ({data.opportunities.length})
                </h3>
                <Button size="sm" onClick={() => setNudgeOpen(true)}>
                  <Send className="h-4 w-4 mr-1" />
                  Enviar e-mail de estímulo
                </Button>
              </div>

              {data.opportunities.length === 0 ? (
                <Card><CardContent className="p-4 text-sm text-muted-foreground">
                  Nenhuma oportunidade detectada — usuário com uso saudável no período.
                </CardContent></Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {data.opportunities.map((o) => (
                    <OpportunityCard
                      key={o.key}
                      o={o}
                      eligible={data.communication.marketing_eligible}
                      name={data.identity.full_name ?? ''}
                    />
                  ))}
                </div>
              )}
            </div>

            {/* Activation funnel */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  Funil de ativação
                  {data.funnel.blocked_step && (
                    <span className="ml-2 text-xs font-normal text-warning">
                      travado em: {FUNNEL_LABELS[data.funnel.blocked_step] ?? data.funnel.blocked_step}
                      {data.funnel.days_stuck !== null && ` (há ${data.funnel.days_stuck}d)`}
                    </span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {data.funnel.steps.map((s) => (
                  <div key={s.key} className="flex items-center gap-2 text-sm">
                    {s.done
                      ? <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
                      : <Circle className="h-4 w-4 text-muted-foreground shrink-0" />}
                    <span className={s.done ? '' : 'text-muted-foreground'}>
                      {FUNNEL_LABELS[s.key] ?? s.key}
                    </span>
                    <span className="ml-auto text-xs text-muted-foreground">{fmt(s.at, 'dd/MM/yy')}</span>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Outreach history */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                  Comunicações de estímulo
                  <Badge variant="outline" className="text-xs">
                    {data.outreach_stats?.sent ?? 0} enviados · {data.outreach_stats?.clicked ?? 0} com clique
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!data.outreach || data.outreach.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhum e-mail de estímulo enviado ainda.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Data</TableHead>
                          <TableHead>Passo</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Clique no CTA</TableHead>
                          <TableHead>Enviado por</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.outreach.map((o) => (
                          <TableRow key={o.id}>
                            <TableCell className="whitespace-nowrap">{fmt(o.created_at, 'dd/MM/yy HH:mm')}</TableCell>
                            <TableCell>{FUNNEL_LABELS[o.funnel_step] ?? o.funnel_step}</TableCell>
                            <TableCell>
                              <Badge
                                variant={o.status === 'sent' ? 'default' : 'destructive'}
                                className={o.status === 'sent' ? 'bg-success text-success-foreground hover:bg-success' : ''}
                              >
                                {o.status === 'sent' ? 'Enviado' : 'Falhou'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {o.clicked_at ? (
                                <span className="text-success text-xs">
                                  {fmt(o.clicked_at, 'dd/MM/yy HH:mm')} ({o.click_count}x)
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">{o.sent_by_name || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>



            {/* Usage KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <KPI icon={HeartPulse} value={data.usage.patients_total} label={`Pacientes (${data.usage.patients_active} ativos)`} />
              <KPI icon={ClipboardList} value={data.usage.care_plan_tasks_active} label="Tarefas de plano ativas" />
              <KPI icon={CalendarClock} value={data.usage.shift_templates} label="Escalas configuradas" />
              <KPI
                icon={Activity}
                value={data.usage.shifts_period_user ?? 0}
                hint={`${data.usage.shifts_period} nos pacientes`}
                label={`Plantões do usuário (${period}d)`}
              />
              <KPI
                icon={CheckCircle2}
                value={data.usage.executions_period_user ?? 0}
                hint={`${data.usage.executions_period} nos pacientes`}
                label={`Execuções do usuário (${period}d)`}
                tone="success"
              />

              <KPI
                icon={Activity}
                value={data.usage.adherence_pct === null ? '—' : `${data.usage.adherence_pct}%`}
                label="Aderência ao plano"
                tone={data.usage.adherence_pct === null ? 'muted' : data.usage.adherence_pct >= 80 ? 'success' : data.usage.adherence_pct >= 60 ? 'warning' : 'destructive'}
              />
              <KPI
                icon={AlertTriangle}
                value={data.usage.late_pct === null ? '—' : `${data.usage.late_pct}%`}
                label="Execuções atrasadas"
                tone={data.usage.late_pct !== null && data.usage.late_pct > 20 ? 'warning' : 'muted'}
              />
              <KPI icon={AlertTriangle} value={data.usage.incidents} label="Intercorrências" tone="destructive" />
              <KPI icon={Users} value={data.team.members_active} label={`Equipe ativa (${data.team.caregivers} cuidadores)`} />
              <KPI icon={Users} value={`${data.team.invites_accepted}/${data.team.invites_sent}`} label="Convites aceitos" tone="muted" />
              <KPI
                icon={Bell}
                value={`${data.communication.notifications_read}/${data.communication.notifications_created}`}
                label="Notificações lidas"
                tone="muted"
              />
              <KPI
                icon={Mail}
                value={`${data.communication.emails_sent}${data.communication.emails_failed ? ` / ${data.communication.emails_failed} falhas` : ''}`}
                label={data.communication.marketing_eligible ? 'E-mails (opt-in ativo)' : 'E-mails (sem opt-in)'}
                tone={data.communication.marketing_eligible ? 'muted' : 'warning'}
              />
            </div>

            {/* Activity chart */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Atividade diária</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Linhas cheias: ações do próprio usuário. Linhas tracejadas: total dos pacientes sob sua responsabilidade.
                </p>
              </CardHeader>
              <CardContent className="px-2 pb-3">
                <div className="h-56 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.usage.activity_series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="date" fontSize={11} tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(v) => format(new Date(v + 'T12:00:00'), 'dd/MM')} />
                      <YAxis allowDecimals={false} fontSize={11} tick={{ fill: 'hsl(var(--muted-foreground))' }} width={36} />
                      <RTooltip
                        contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 8, color: 'hsl(var(--foreground))' }}
                        labelFormatter={(v) => format(new Date(v + 'T12:00:00'), 'dd/MM/yyyy')}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="executions_user" name="Execuções do usuário" stroke="hsl(var(--primary))" fill="hsl(var(--primary) / 0.25)" strokeWidth={2} />
                      <Area type="monotone" dataKey="shifts_user" name="Plantões do usuário" stroke="hsl(var(--success))" fill="hsl(var(--success) / 0.2)" strokeWidth={2} />
                      <Area type="monotone" dataKey="executions" name="Execuções nos pacientes" stroke="hsl(var(--muted-foreground))" fill="hsl(var(--muted-foreground) / 0.08)" strokeWidth={1.5} strokeDasharray="4 3" />
                      <Area type="monotone" dataKey="shifts" name="Plantões nos pacientes" stroke="hsl(var(--muted-foreground))" fill="transparent" strokeWidth={1.5} strokeDasharray="2 3" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>


            {/* Care plan composition */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Composição do plano de cuidado</CardTitle></CardHeader>
              <CardContent>
                {Object.keys(data.usage.care_plan_by_type).length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma tarefa ativa no plano.</p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    {Object.entries(data.usage.care_plan_by_type).map(([k, v]) => (
                      <div key={k} className="p-3 rounded bg-muted">
                        <p className="font-bold text-lg text-primary">{v}</p>
                        <p className="text-muted-foreground text-xs">{TASK_TYPE_LABELS[k] ?? k}</p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Patients */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Pacientes ({data.patients.length})</CardTitle></CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto scrollbar-none">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Paciente</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-center">Tarefas plano</TableHead>
                        <TableHead className="text-center">Escalas</TableHead>
                        <TableHead className="text-center">Plantões (usuário / total)</TableHead>
                        <TableHead className="text-center">Execuções (usuário / total)</TableHead>

                        <TableHead className="text-center">Aderência</TableHead>
                        <TableHead>Última atividade</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.patients.map((p) => (
                        <TableRow key={p.patient_id}>
                          <TableCell className="text-sm">
                            <span className="truncate max-w-[160px] inline-block">{p.nickname}</span>
                            {p.is_owner && <Badge variant="outline" className="ml-1 text-xs">dono</Badge>}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{p.status}</Badge>
                          </TableCell>
                          <TableCell className="text-center text-sm">{p.plan_tasks}</TableCell>
                          <TableCell className="text-center text-sm">{p.templates}</TableCell>
                          <TableCell className="text-center text-sm">
                            {p.shifts_period_user ?? 0}
                            <span className="text-xs text-muted-foreground"> / {p.shifts_period}</span>
                          </TableCell>
                          <TableCell className="text-center text-sm">
                            {p.executions_period_user ?? 0}
                            <span className="text-xs text-muted-foreground"> / {p.executions_period}</span>
                          </TableCell>

                          <TableCell className="text-center text-sm">
                            {p.adherence_pct === null ? '—' : `${p.adherence_pct}%`}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{fmt(p.last_activity_at)}</TableCell>
                        </TableRow>
                      ))}
                      {data.patients.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-6">
                            Nenhum paciente vinculado.
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Timeline */}
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Linha do tempo</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {data.timeline.map((t, i) => (
                  <div key={`${t.type}-${t.at}-${i}`} className="flex items-start gap-2 text-sm">
                    <span className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />
                    <span className="flex-1">{t.label}</span>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{fmt(t.at)}</span>
                  </div>
                ))}
                {data.timeline.length === 0 && (
                  <p className="text-sm text-muted-foreground">Sem eventos registrados.</p>
                )}
              </CardContent>
            </Card>

            <ActivationNudgeDialog
              open={nudgeOpen}
              onOpenChange={setNudgeOpen}
              userId={data.identity.user_id}
              userName={data.identity.full_name}
              userEmail={data.identity.email}
              defaultStep={data.funnel.blocked_step}
              daysStuck={data.funnel.days_stuck}
            />
          </>

        ) : null}
      </div>
    </AppLayout>
  );
}
